import React, { useState } from 'react';
import { CoreMeitnerPlacementConfiguration, CoreMeitnerUserConfiguration, defaultMeitnerPlacementConfiguration, meitnerMobilePhoneNumberExportTypeOptions } from '../../../../../../models/CoreMeitnerModels';
import CheckboxListItem from '../../../../../misc/CheckboxListItem';
import NullableIntInputCheckboxText from '../../../../../misc/NullableIntInputCheckboxText';
import EnumInput from '../../../../../misc/EnumInput';

import { useTranslation } from 'react-i18next';
import { Card, CardHeader, Button, Collapse, CardBody } from 'reactstrap'


interface MeitnerUserProps {
    user: CoreMeitnerUserConfiguration
    disabled: boolean
    name: string
    updateValue: (x: CoreMeitnerUserConfiguration) => void
    displayName: string
    validation: string[],
    isGuardianConfig?: boolean
    // Only given for students, whose placements have a lifecycle of their own.
    placement?: CoreMeitnerPlacementConfiguration
    updatePlacement?: (x: CoreMeitnerPlacementConfiguration) => void
}


const MeitnerUser = (props: MeitnerUserProps) => {
    const { t } = useTranslation(['configuration']);
    const [isOpen, setIsOpen] = useState<boolean>(true);

    const updateConfig = (change: Partial<CoreMeitnerUserConfiguration>) =>
        props.updateValue({ ...props.user, ...change });

    const placement = props.placement ?? defaultMeitnerPlacementConfiguration;

    // A checked "no archiving" box (null) turns archiving off but keeps the days.
    // An empty field (undefined) becomes NaN so the validator rejects it.
    const updateArchiveAfterDays = (value: number | null | undefined) => props.updatePlacement?.(value === null
        ? {
            archiveEnabled: false,
            archiveAfterDays: Number.isInteger(placement.archiveAfterDays) ? placement.archiveAfterDays : defaultMeitnerPlacementConfiguration.archiveAfterDays
        }
        : { archiveEnabled: true, archiveAfterDays: value ?? NaN });

    return (
        <Card className="mb-3">
            <CardHeader tag="h5">
                <Button outline color="secondary" style={{ width: '30px', float: 'right', lineHeight: '0.8', padding: '0.5rem' }} onClick={() => setIsOpen(!isOpen)}>{isOpen ? '-' : '+'}</Button>
                {props.displayName}
            </CardHeader>
            <Collapse isOpen={isOpen}>
                <CardBody>

                    <CheckboxListItem
                        text={t('meitnerExportConfiguration.sendAddress', { ns: 'configuration' })}
                        id="sendAddress"
                        name="sendAddress"
                        value={props.user.sendAddress}
                        disabled={props.disabled}
                        onChange={(e) => updateConfig({ sendAddress: e.target.checked })}
                    />

                    <CheckboxListItem
                        text={t('meitnerExportConfiguration.sendEmailAddress', { ns: 'configuration' })}
                        id="sendEmailAddress"
                        name="sendEmailAddress"
                        value={props.user.sendEmailAddress}
                        disabled={props.disabled}
                        onChange={(e) => updateConfig({ sendEmailAddress: e.target.checked })}
                    />
                {!props.isGuardianConfig ? (
                    <CheckboxListItem
                        text={t('meitnerExportConfiguration.sendTelephoneNumber', { ns: 'configuration' })}
                        id="sendMobileNumber"
                        name="sendMobileNumber"
                        value={props.user.sendTelephoneNumber}
                        disabled={props.disabled}
                        onChange={(e) => updateConfig({ sendTelephoneNumber: e.target.checked })}
                    /> )
                    :
                    (
                    <div className='mt-2'>
                    <EnumInput
                        title={t('meitnerExportConfiguration.mobilePhoneNumberExportType.title', { ns: 'configuration' })}
                        titleSmall = {true}
                        value={props.user.sendMobilePhoneNumber}
                        options={meitnerMobilePhoneNumberExportTypeOptions.map(x => ({
                            value: x,
                            text: t(`meitnerExportConfiguration.mobilePhoneNumberExportType.options.${x}`, { ns: 'configuration' }),
                        }))}
                        onChange={(name, value) => updateConfig({ [name]: value })}
                        disabled={props.disabled}
                        name='sendMobilePhoneNumber'
                    />
                     </div>
                     )
                    }

                    <div className="mt-4">
                        <NullableIntInputCheckboxText
                            title={t('meitnerExportConfiguration.gracePeriod.title', { ns: 'configuration' })}
                            value={props.user.gracePeriod}
                            name={`${props.name}GracePeriod`}
                            disabled={props.disabled}
                            // A checked "no deletion" box (null) never deletes. An empty field (undefined) becomes NaN so the validator rejects it.
                            onChange={(_, value) => updateConfig({ gracePeriod: value === undefined ? NaN : value })}
                            defaultValue={0}
                            checkboxTitle={t('meitnerExportConfiguration.gracePeriod.noDelete', { ns: 'configuration' })}
                            invalid={props.validation.indexOf(`${props.name}.gracePeriod`) !== -1 }
                            invalidFeedback={t('meitnerExportConfiguration.gracePeriod.invalidFeedback', { ns: 'configuration' })}
                        />
                    </div>

                    {props.updatePlacement && (<>
                        <h6 className="mt-4">{t('meitnerExportConfiguration.studentPlacementTitle', { ns: 'configuration' })}</h6>
                        <NullableIntInputCheckboxText
                            title={<>
                                {t('meitnerExportConfiguration.studentPlacementArchive.title', { ns: 'configuration' })}
                                <small className="d-block">{t('meitnerExportConfiguration.studentPlacementArchive.description', { ns: 'configuration' })}</small>
                            </>}
                            value={placement.archiveEnabled ? placement.archiveAfterDays : null}
                            name="studentPlacementArchiveAfterDays"
                            disabled={props.disabled}
                            onChange={(_, value) => updateArchiveAfterDays(value)}
                            defaultValue={defaultMeitnerPlacementConfiguration.archiveAfterDays}
                            checkboxTitle={t('meitnerExportConfiguration.studentPlacementArchive.checkboxTitle', { ns: 'configuration' })}
                            invalid={props.validation.indexOf('studentPlacementConfiguration.archiveAfterDays') !== -1}
                            invalidFeedback={t('meitnerExportConfiguration.studentPlacementArchive.invalidFeedback', { ns: 'configuration' })}
                        />
                    </>)}

                </CardBody>
            </Collapse>
        </Card>
    );
};

export default MeitnerUser;
